import { useEffect, useMemo, useState } from "react";
import BackingPrep from "@/components/creator/sing/BackingPrep";
import useBacking from "@/components/creator/sing/useBacking";
import TakePicker from "@/components/creator/replace/TakePicker";
import LineUpPreview from "@/components/creator/replace/LineUpPreview";
import MixControls from "@/components/creator/replace/MixControls";
import TouchUps from "@/components/creator/replace/TouchUps";
import ReplaceJob from "@/components/creator/replace/ReplaceJob";
import useLineUpPreview from "@/components/creator/replace/useLineUpPreview";
import useVocalJob from "@/components/creator/replace/useVocalJob";

function Step({ n, title, hint, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-6">
      <p className="flex items-center gap-3 font-display text-lg font-semibold">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-amber-300/15 font-mono text-[12px] text-amber-200">{n}</span>{title}
      </p>
      {hint ? <p className="mb-5 mt-1 pl-10 text-[12px] text-white/50">{hint}</p> : <div className="mb-5" />}
      {children}
    </section>
  );
}

const extraUrl = (backing, mode) => (mode === "partner" ? backing?.partner?.url : mode === "full" ? backing?.vocals?.url : null);

/** Replace one version's AI lead with your take: pick it, line it up, touch it up, render a new version. */
export default function ReplaceSession({ project, version, initialTakeId, onProject }) {
  const { backing, prepare, busy } = useBacking(project, version);
  const ready = backing?.status === "done";
  const takes = useMemo(() => (project.takes || []).filter((t) => t.version_id === version.id)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))), [project.takes, version.id]);
  const [takeId, setTakeId] = useState(initialTakeId || null);
  useEffect(() => { if (takes.length && !takes.some((t) => t.id === takeId)) setTakeId(takes[0].id); }, [takes, takeId]);
  const take = takes.find((t) => t.id === takeId) || null;
  const [settings, setSettings] = useState({ backing_mode: null, shift_ms: 0, vocal_db: 0, music_db: 0, denoise: true, tune: "natural", scale: project.key ? "key" : "chromatic" });
  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));
  useEffect(() => {
    if (ready) setSettings((s) => (s.backing_mode ? s : { ...s, backing_mode: backing.arrangement_type === "duet" && backing.partner ? "partner" : "instrumental" }));
  }, [ready, backing]);
  const { job, start, busy: starting } = useVocalJob(project.id, version.id, onProject);
  const running = job?.status === "running";
  const preview = useLineUpPreview({ musicUrl: ready ? backing.instrumental?.url : null, extraUrl: ready ? extraUrl(backing, settings.backing_mode) : null,
    vocalUrl: take?.vocal?.url, shiftMs: settings.shift_ms, vocalDb: settings.vocal_db, musicDb: settings.music_db });
  const { stop } = preview;
  useEffect(() => { if (running) stop(); }, [running, stop]);

  if (!ready) return <BackingPrep backing={backing} onPrepare={prepare} busy={busy} />;
  return (
    <div className="space-y-6">
      <Step n={1} title="Pick your take" hint="Only your own recorded voice goes in — the AI lead comes out." testId="replace-step-take">
        <TakePicker projectId={project.id} versionId={version.id} takes={takes} takeId={takeId} onPick={setTakeId} onProject={onProject} disabled={running} />
      </Step>
      {take ? (
        <>
          <Step n={2} title="Line it up & balance" hint="Play it, then nudge the timing if your voice feels early or late." testId="replace-step-mix">
            <div className="space-y-6"><LineUpPreview preview={preview} disabled={running} /><MixControls backing={backing} settings={settings} onChange={set} disabled={running} /></div>
          </Step>
          <Step n={3} title="Touch-ups" hint="Applied to your vocal only — the music is never changed." testId="replace-step-touchups">
            <TouchUps settings={settings} onChange={set} projectKey={project.key} disabled={running} />
          </Step>
        </>
      ) : null}
      <ReplaceJob job={job} busy={starting} canStart={Boolean(take && settings.backing_mode)} project={project} original={version}
        onStart={() => start({ take_id: take.id, ...settings })} />
    </div>
  );
}
